import React, { useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useSimulatorStore } from '../../store/simulatorStore';
import ExperimentNode from '../nodes/ExperimentNode';
import { ExperimentNodeData } from '../../types';
import { getComponentDef } from '../../engine/componentLibrary';
import { ComponentType } from '../../types';

const NODE_TYPES = { experimentNode: ExperimentNode };

function WorkspaceInner() {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    setSelectedNodeId,
  } = useSimulatorStore();

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  // ── Drag-and-drop from library ──────────────────────────────────────────
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('componentType') as ComponentType;
      if (!type) return;

      const def = getComponentDef(type);
      if (!def) return;

      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });

      const id = `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      addNode({
        id,
        type: 'experimentNode',
        position,
        data: {
          componentType: def.type,
          label: def.label,
          category: def.category,
          icon: def.icon,
          description: def.description,
          tooltip: def.tooltip,
          ageMode: def.ageMode,
          properties: { ...def.defaultProperties },
          inputs: def.inputs,
          outputs: def.outputs,
        } as ExperimentNodeData,
      });
    },
    [addNode, screenToFlowPosition]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      setSelectedNodeId(node.id);
    },
    [setSelectedNodeId]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, [setSelectedNodeId]);

  return (
    <div
      ref={reactFlowWrapper}
      style={{ flex: 1, position: 'relative', background: '#f0f4ff' }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        defaultEdgeOptions={{
          animated: true,
          style: { stroke: '#6366f1', strokeWidth: 2 },
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1.5}
          color="#c7d2fe"
        />
        <Controls
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
          }}
        />
        <MiniMap
          nodeColor={(node) => {
            const def = getComponentDef(
              (node.data as ExperimentNodeData)?.componentType
            );
            return def?.color ?? '#6366f1';
          }}
          style={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
        />

        {/* Empty state hint */}
        {nodes.length === 0 && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔬</div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#64748b',
                marginBottom: 6,
              }}
            >
              Build Your Experiment!
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', maxWidth: 280 }}>
              Drag components from the left panel, or choose a preset experiment
              from the top bar.
            </div>
          </div>
        )}
      </ReactFlow>
    </div>
  );
}

export default function Workspace() {
  return (
    <ReactFlowProvider>
      <WorkspaceInner />
    </ReactFlowProvider>
  );
}
